import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../../utils/cn';

/**
 * Two tab flavours, deliberately NOT sharing a look.
 *
 * `NavTabs` switches routes: a full-width underline bar, the module's section
 * menu, sitting directly under the page header. `Tabs` switches state inside
 * one page: a segmented pill group, inset in the content column.
 *
 * They used to be identical, and on any page carrying both — /spray/admin,
 * /spray/inventory — that produced two full-width underline bars stacked one
 * under the other, reading as a double header with no way to tell which one
 * navigated away from the page. The level of a control has to be visible from
 * the control.
 *
 * `NavTabs` scrolls sideways instead of wrapping: a wrapped underline bar
 * reads as two rows of buttons and the underline stops lining up with
 * anything. `Tabs` wraps, because a pill group has no underline to misalign
 * and six sections do not fit one line on a laptop, let alone a phone —
 * scrolling them would hide whole tabs behind a clipped rounded edge with
 * nothing to say they were there.
 */

const barClass =
  // The divider is an inset shadow rather than a border so the active tab's
  // own border can sit on top of it without a -mb-px, which would make the
  // scroll container scroll vertically by a pixel.
  'flex overflow-x-auto shadow-[inset_0_-1px_0_0_theme(colors.neutral.200)] [scrollbar-width:none]';

// `w-fit max-w-full` keeps the group as wide as its tabs and no wider — a
// pill track spanning the full column would read as a bar again — while still
// wrapping inside the column rather than pushing the page sideways.
const pillBarClass = 'flex w-fit max-w-full flex-wrap gap-1 rounded-xl bg-neutral-100 p-1';

function tabClass(active: boolean) {
  return cn(
    'flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors sm:px-4',
    active
      ? 'border-primary-600 text-primary-700'
      : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-800'
  );
}

function pillClass(active: boolean) {
  return cn(
    'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
    active
      ? 'bg-white text-primary-700 shadow-sm'
      : 'text-neutral-500 hover:text-neutral-800'
  );
}

export interface TabItem<K extends string> {
  key: K;
  label: string;
  icon?: ReactNode;
}

interface TabsProps<K extends string> {
  tabs: TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
  className?: string;
}

export function Tabs<K extends string>({ tabs, active, onChange, className }: TabsProps<K>) {
  return (
    <div role="tablist" className={cn(pillBarClass, className)}>
      {tabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={active === tab.key}
          onClick={() => onChange(tab.key)}
          className={pillClass(active === tab.key)}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export interface NavTabItem {
  to: string;
  label: string;
  icon?: ReactNode;
  /** Match the path exactly, so a module root is not active on every sub-page. */
  end?: boolean;
}

export function NavTabs({ items, className, label }: { items: NavTabItem[]; className?: string; label: string }) {
  return (
    <nav aria-label={label} className={cn(barClass, className)}>
      {items.map(item => (
        <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => tabClass(isActive)}>
          {item.icon}
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}
